import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import {
  Label,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ITsaReportState,
  ITsaResult,
} from '../../../data/providers/reports/tsaReportProvider';
import { ISize } from '../../../data/types';
import { EPdfMode } from '../../shared/pdf/Pdf';
import { quartileColors } from '../utils/quartileColors';
import { TsaTooltip } from './TsaTooltip';

interface IProps {
  reportState: ITsaReportState;
  pdfMode?: EPdfMode;
  quartileColors: string[];
}

export const TsaGraph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): {
    tsaVals: ITsaResult[];
    median: number;
    highQ: number;
    lowQ: number;
  } => {
    return {
      tsaVals: props.reportState.tsaResults,
      median: props.reportState.quartileData.median,
      highQ: props.reportState.quartileData.highQuartile,
      lowQ: props.reportState.quartileData.lowQuartile,
    };
  }, [JSON.stringify(props.reportState.tsaResults)]);

  return (
    <React.Fragment>
      <div
        style={{
          height: '100%',
          width: '100%',
          paddingBottom: '10px',
        }}
        className='graph-parent'
      >
        <AutoSizer>
          {(size: ISize): React.ReactElement => {
            return (
              <ScatterChart
                margin={{ top: 5, left: 10, right: 10, bottom: 10 }}
                height={size.height}
                width={size.width}
              >
                {/* Axes definitions */}
                <XAxis
                  dataKey='tsa'
                  domain={data.median > 4 ? [0, 100] : [-4, 4]}
                  name='TSA'
                  type='number'
                >
                  <Label
                    fill='var(--color-text)'
                    offset={-10}
                    position='insideBottom'
                    strokeOpacity={0.1}
                    value='TSA Score'
                  />
                </XAxis>
                <YAxis type='number' dataKey='grouping' domain={[0, 2]} hide />
                <ReferenceLine y={0} stroke='#ccc' strokeOpacity={0.25} />
                {/* Reference area for quartile 3 */}
                <ReferenceArea
                  fill={quartileColors[1]}
                  fillOpacity={2}
                  stroke='var(--color-text)'
                  strokeOpacity={1}
                  x1={data.median}
                  x2={data.highQ}
                  y1={0.8}
                  y2={1.2}
                />
                <ReferenceLine
                  segment={[
                    { x: data.highQ, y: 0.8 },
                    { x: data.highQ, y: 1.2 },
                  ]}
                >
                  <Label
                    fill='var(--color-text)'
                    fillOpacity={1}
                    position='top'
                    value={data.highQ}
                  />
                  <Label
                    fill='var(--color-text)'
                    fillOpacity={1}
                    position='bottom'
                    value={'Q3'}
                  />
                </ReferenceLine>
                {/* Reference area for quartile 2 */}
                <ReferenceArea
                  fill={quartileColors[2]}
                  fillOpacity={2}
                  stroke='var(--color-text)'
                  strokeOpacity={1}
                  x1={data.lowQ}
                  x2={data.median}
                  y1={0.8}
                  y2={1.2}
                />
                <ReferenceLine
                  segment={[
                    { x: data.lowQ, y: 0.8 },
                    { x: data.lowQ, y: 1.2 },
                  ]}
                >
                  <Label
                    fill='var(--color-text)'
                    fillOpacity={1}
                    position='top'
                    value={data.lowQ}
                  />
                  <Label
                    fill='var(--color-text)'
                    fillOpacity={1}
                    position='bottom'
                    value={'Q2'}
                  />
                </ReferenceLine>
                {/* Reference line for the median */}
                <ReferenceLine
                  isFront
                  strokeOpacity={1}
                  segment={[
                    { x: data.median, y: 0.3 },
                    { x: data.median, y: 1.7 },
                  ]}
                  stroke='var(--color-text)'
                >
                  <Label
                    value={`Median: ${data.median}`}
                    fill='var(--color-text)'
                    position='top'
                    strokeOpacity={0.1}
                  />
                </ReferenceLine>
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={TsaTooltip}
                />
                <Scatter
                  data={data.tsaVals}
                  fill={
                    props.pdfMode === EPdfMode.PRINT
                      ? 'var(--color-scatter-primary-inverse)'
                      : 'var(--color-scatter-primary)'
                  }
                  stroke='var(--color-separator)'
                />
              </ScatterChart>
            );
          }}
        </AutoSizer>
      </div>
    </React.Fragment>
  );
};

// const CustomDot = (props: any): React.ReactElement => {
//   const [order] = (props.key as string).match(/[0-9]+$/);
//   return (
//     <g id={`point-${order}`}>
//       <circle
//         style={{ cursor: 'pointer' }}
//         cx={props.cx}
//         cy={props.cy}
//         fill={props.fill}
//         height={props.height}
//         stroke='var(--color-text)'
//         width={props.width}
//         y={props.y}
//         x={props.x}
//         r={15}
//       ></circle>
//       <text
//         textAnchor='middle'
//         fill='transparent'
//         stroke='var(--color-secondary-text)'
//         fontSize='0.8em'
//         y={props.y + 8}
//         x={props.x + 5}
//         cx={props.cx + 5}
//         cy={props.cy + 8}
//       >
//         {+order + 1}
//       </text>
//     </g>
//   );
// };
